import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  InputAdornment,
  Select,
  MenuItem,
  IconButton,
  Snackbar,
  Tooltip,
} from '@mui/material';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { fetchUsers, createUser, updateUser, resetUserPassword } from '../services/api';
import ExpandableRecordList from '../components/ExpandableRecordList';
import { useAuth } from '../context/AuthContext';
import { formatExpiryWithTime } from '../utils/time';

const ROLE_LABELS = {
  admin: 'Admin',
  technician: 'Technician',
  accounting: 'Accounting',
  bu_manager: 'Business Unit Admin',
  standard: 'Standard',
};

const ROLE_COLORS = {
  admin: 'error',
  technician: 'primary',
  accounting: 'warning',
  bu_manager: 'success',
  standard: 'default',
};

const EMPTY_FORM = {
  firstName: '',
  lastName: '',
  email: '',
  password: '',
  role: 'standard',
  country: '',
  businessUnit: '',
  region: '',
};

function ManageUsersPage() {
  const auth = useAuth() || {};
  // BU admins manage only Standard/BUA accounts inside their own BU; the
  // backend enforces the same rules server-side.
  const isBua = auth.user?.role === 'bu_manager';
  const buaCanManage = (u) => ['standard', 'bu_manager'].includes(u.role);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [addUserOpen, setAddUserOpen] = useState(false);
  const [addUserForm, setAddUserForm] = useState(EMPTY_FORM);
  const [addUserError, setAddUserError] = useState('');
  const [addUserSaving, setAddUserSaving] = useState(false);
  const [showAddUserPassword, setShowAddUserPassword] = useState(false);

  const [editUser, setEditUser] = useState(null);
  const [editForm, setEditForm] = useState({ role: 'standard', country: '', businessUnit: '', region: '' });
  const [editError, setEditError] = useState('');
  const [editSaving, setEditSaving] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [actionBusy, setActionBusy] = useState(false);
  const [resetResult, setResetResult] = useState(null);
  const [copySnack, setCopySnack] = useState(false);

  const [availableCountriesList, setAvailableCountriesList] = useState([]);
  const [availableBusinessUnits, setAvailableBusinessUnits] = useState([]);
  const [availableRegions, setAvailableRegions] = useState([]);

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await fetchUsers();
      setUsers(data);
    } catch (e) {
      setError(e.message || 'Failed to load users');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const sortedUsers = useMemo(() => {
    const collator = new Intl.Collator(undefined, { sensitivity: 'base' });
    const byName = (a, b) => collator.compare(a.fullName || a.username || '', b.fullName || b.username || '');
    const active = users.filter(u => u.isActive !== false).sort(byName);
    const deleted = users.filter(u => u.isActive === false).sort(byName);
    return [...active, ...deleted];
  }, [users]);

  const handleAddUserOpen = () => {
    const tempPassword = Math.floor(Math.random() * 1000000).toString().padStart(6, '0');
    if (isBua) {
      // Country/BU are locked to the BUA's own; only region is choosable
      setAddUserForm({
        ...EMPTY_FORM,
        password: tempPassword,
        country: auth.user?.country || '',
        businessUnit: auth.user?.businessUnit || '',
      });
      const params = new URLSearchParams();
      if (auth.user?.businessUnit) params.set('businessUnit', auth.user.businessUnit);
      if (auth.user?.country) params.set('country', auth.user.country);
      const qs = params.toString();
      fetch(`/api/regions${qs ? `?${qs}` : ''}`)
        .then(r => r.json()).then(d => setAvailableRegions(d.regions || [])).catch(() => {});
    } else {
      setAddUserForm({ ...EMPTY_FORM, password: tempPassword });
      if (availableCountriesList.length === 0) {
        fetch('/api/countries').then(r => r.json()).then(d => setAvailableCountriesList(d.countries || [])).catch(() => {});
      }
      if (availableBusinessUnits.length === 0) {
        fetch('/api/business-units').then(r => r.json()).then(d => setAvailableBusinessUnits(d.businessUnits || [])).catch(() => {});
      }
    }
    setAddUserError('');
    setShowAddUserPassword(true);
    setAddUserOpen(true);
  };

  const handleAddUserClose = () => {
    setAddUserOpen(false);
    setAddUserError('');
  };

  const handleEditOpen = (u) => {
    if (isBua && !buaCanManage(u)) return;
    setEditUser(u);
    setEditForm({
      role: u.role || 'standard',
      country: u.country || '',
      businessUnit: u.businessUnit || '',
      region: u.region || '',
    });
    setEditError('');
    if (availableCountriesList.length === 0) {
      fetch('/api/countries').then(r => r.json()).then(d => setAvailableCountriesList(d.countries || [])).catch(() => {});
    }
    const buQs = u.country ? `?country=${encodeURIComponent(u.country)}` : '';
    fetch(`/api/business-units${buQs}`).then(r => r.json()).then(d => setAvailableBusinessUnits(d.businessUnits || [])).catch(() => {});
    const regionParams = new URLSearchParams();
    if (u.businessUnit) regionParams.set('businessUnit', u.businessUnit);
    if (u.country) regionParams.set('country', u.country);
    const regionQs = regionParams.toString();
    fetch(`/api/regions${regionQs ? `?${regionQs}` : ''}`).then(r => r.json()).then(d => setAvailableRegions(d.regions || [])).catch(() => {});
  };

  const handleEditClose = () => {
    setEditUser(null);
    setEditError('');
  };

  const handleDeleteUser = async () => {
    if (!editUser) return;
    setActionBusy(true);
    setEditError('');
    try {
      await updateUser(editUser.id, { isActive: false });
      setDeleteConfirmOpen(false);
      handleEditClose();
      loadUsers();
    } catch (e) {
      setEditError(e.message || 'Failed to delete user.');
      setDeleteConfirmOpen(false);
    } finally {
      setActionBusy(false);
    }
  };

  const handleRestoreUser = async () => {
    if (!editUser) return;
    setActionBusy(true);
    setEditError('');
    try {
      await updateUser(editUser.id, { isActive: true });
      handleEditClose();
      loadUsers();
    } catch (e) {
      setEditError(e.message || 'Failed to restore user.');
    } finally {
      setActionBusy(false);
    }
  };

  const handleResetPassword = async () => {
    if (!editUser) return;
    setActionBusy(true);
    setEditError('');
    try {
      const res = await resetUserPassword(editUser.id);
      setResetResult({ user: editUser, password: res.newPassword });
    } catch (e) {
      setEditError(e.message || 'Failed to reset password.');
    } finally {
      setActionBusy(false);
    }
  };

  const handleCopyPassword = async () => {
    if (!resetResult) return;
    try {
      await navigator.clipboard.writeText(resetResult.password);
      setCopySnack(true);
    } catch {
      /* clipboard unavailable */
    }
  };

  const handleEditSubmit = async () => {
    if (!editUser) return;
    setEditSaving(true);
    setEditError('');
    try {
      await updateUser(editUser.id, {
        role: editForm.role,
        country: editForm.country || null,
        businessUnit: editForm.businessUnit || null,
        region: editForm.region || null,
      });
      handleEditClose();
      loadUsers();
    } catch (e) {
      setEditError(e.message || 'Failed to update user.');
    } finally {
      setEditSaving(false);
    }
  };

  const handleAddUserSubmit = async () => {
    if (!addUserForm.email.trim() || !addUserForm.password.trim()) {
      setAddUserError('Email and password are required.');
      return;
    }
    setAddUserSaving(true);
    setAddUserError('');
    try {
      await createUser({
        ...addUserForm,
        username: addUserForm.email,
        fullName: `${addUserForm.firstName} ${addUserForm.lastName}`.trim(),
        country: addUserForm.country || null,
        businessUnit: addUserForm.businessUnit || null,
        region: addUserForm.region || null,
      });
      handleAddUserClose();
      loadUsers();
    } catch (e) {
      setAddUserError(e.message || 'Failed to create user.');
    } finally {
      setAddUserSaving(false);
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1, gap: 2, flexWrap: 'wrap' }}>
        <Box>
          <Typography variant="h4" fontWeight={700} sx={{ mb: 3 }}>
            Manage Users
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={handleAddUserOpen}
        >
          Add User
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
      )}

      {/* Phone/tablet: key fields per row, tap to reveal the rest */}
      <Box sx={{ display: { xs: 'block', md: 'none' } }}>
        {loading ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}><CircularProgress size={24} /></Paper>
        ) : (
          <ExpandableRecordList
            emptyText="No users found."
            records={sortedUsers.map((u) => {
              const deleted = u.isActive === false;
              const canOpen = !isBua || buaCanManage(u);
              return {
                id: u.id,
                title: u.fullName || '—',
                subtitle: [ROLE_LABELS[u.role] || u.role, u.businessUnit || 'No business unit'].join(' · '),
                muted: deleted,
                badge: deleted
                  ? <Chip label="Deleted" size="small" variant="outlined" />
                  : null,
                details: [
                  ['User', u.username],
                  ['Region', u.region || '—'],
                  ['Country', u.country || '—'],
                  ['Created By', u.createdBy || '—'],
                  ['Last Login', u.lastLogin ? formatExpiryWithTime(u.lastLogin, auth.user) : 'Never'],
                ],
                onOpen: canOpen ? () => handleEditOpen(u) : null,
                openLabel: 'Edit user',
              };
            })}
          />
        )}
      </Box>

      <TableContainer component={Paper} sx={{ display: { xs: 'none', md: 'block' } }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 600 }}>Full Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>User</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Region</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Business Unit</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Country</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Created By</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Last Login</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4 }}>
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : sortedUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                  No users found.
                </TableCell>
              </TableRow>
            ) : (
              sortedUsers.map((u) => {
                const deleted = u.isActive === false;
                const canOpen = !isBua || buaCanManage(u);
                return (
                  <TableRow
                    key={u.id}
                    hover={canOpen}
                    onClick={() => handleEditOpen(u)}
                    sx={{ cursor: canOpen ? 'pointer' : 'default', opacity: deleted ? 0.55 : 1 }}
                  >
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box component="span" sx={{ textDecoration: deleted ? 'line-through' : 'none' }}>
                          {u.fullName || '—'}
                        </Box>
                        {deleted && (
                          <Chip label="Deleted" size="small" color="default" variant="outlined" />
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>{u.username}</TableCell>
                    <TableCell>
                      <Chip
                        label={ROLE_LABELS[u.role] || u.role}
                        size="small"
                        color={ROLE_COLORS[u.role] || 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>{u.region || '—'}</TableCell>
                    <TableCell>{u.businessUnit || '—'}</TableCell>
                    <TableCell>{u.country || '—'}</TableCell>
                    <TableCell>{u.createdBy || '—'}</TableCell>
                    <TableCell>{u.lastLogin ? formatExpiryWithTime(u.lastLogin, auth.user) : 'Never'}</TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={addUserOpen} onClose={handleAddUserClose} maxWidth="xs" fullWidth>
        <DialogTitle>Add User</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box sx={{ display: 'flex', gap: 1 }}>
              <TextField
                label="First Name"
                size="small"
                fullWidth
                value={addUserForm.firstName}
                onChange={(e) => setAddUserForm(p => ({ ...p, firstName: e.target.value }))}
              />
              <TextField
                label="Last Name"
                size="small"
                fullWidth
                value={addUserForm.lastName}
                onChange={(e) => setAddUserForm(p => ({ ...p, lastName: e.target.value }))}
              />
            </Box>
            <TextField
              label="Email"
              type="email"
              size="small"
              fullWidth
              value={addUserForm.email}
              onChange={(e) => setAddUserForm(p => ({ ...p, email: e.target.value }))}
              autoComplete="off"
              inputProps={{ autoComplete: 'off' }}
            />
            {isBua ? (
              <>
                <TextField label="Country" size="small" fullWidth disabled value={addUserForm.country || '—'} />
                <TextField label="Business Unit" size="small" fullWidth disabled value={addUserForm.businessUnit || '—'} />
              </>
            ) : (
              <>
                <FormControl size="small" fullWidth>
                  <InputLabel>Country</InputLabel>
                  <Select
                    value={addUserForm.country}
                    label="Country"
                    onChange={(e) => {
                      const c = e.target.value;
                      setAddUserForm(p => ({ ...p, country: c, businessUnit: '' }));
                      fetch(`/api/business-units${c ? `?country=${encodeURIComponent(c)}` : ''}`)
                        .then(r => r.json()).then(d => setAvailableBusinessUnits(d.businessUnits || [])).catch(() => {});
                    }}
                    MenuProps={{ disablePortal: true }}
                  >
                    {availableCountriesList.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Business Unit</InputLabel>
                  <Select
                    value={addUserForm.businessUnit}
                    label="Business Unit"
                    onChange={(e) => {
                      const bu = e.target.value;
                      setAddUserForm(p => ({ ...p, businessUnit: bu, region: '' }));
                      const params = new URLSearchParams();
                      if (bu) params.set('businessUnit', bu);
                      if (addUserForm.country) params.set('country', addUserForm.country);
                      const qs = params.toString();
                      fetch(`/api/regions${qs ? `?${qs}` : ''}`)
                        .then(r => r.json()).then(d => setAvailableRegions(d.regions || [])).catch(() => {});
                    }}
                    MenuProps={{ disablePortal: true }}
                  >
                    {availableBusinessUnits.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                  </Select>
                </FormControl>
              </>
            )}
            <FormControl size="small" fullWidth>
              <InputLabel>Region</InputLabel>
              <Select
                value={addUserForm.region}
                label="Region"
                onChange={(e) => setAddUserForm(p => ({ ...p, region: e.target.value }))}
                MenuProps={{ disablePortal: true }}
              >
                {[...availableRegions, ...(!availableRegions.includes('General') ? ['General'] : [])].map(r => (
                  <MenuItem key={r} value={r}>{r}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Password"
              type={showAddUserPassword ? 'text' : 'password'}
              size="small"
              fullWidth
              value={addUserForm.password}
              onChange={(e) => setAddUserForm(p => ({ ...p, password: e.target.value }))}
              autoComplete="new-password"
              inputProps={{ autoComplete: 'new-password' }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowAddUserPassword(s => !s)}
                      edge="end"
                      size="small"
                      tabIndex={-1}
                      aria-label={showAddUserPassword ? 'Hide password' : 'Show password'}
                    >
                      {showAddUserPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <FormControl size="small" fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={addUserForm.role}
                label="Role"
                onChange={(e) => setAddUserForm(p => ({ ...p, role: e.target.value }))}
                MenuProps={{ disablePortal: true }}
              >
                <MenuItem value="standard">Standard</MenuItem>
                {!isBua && <MenuItem value="technician">Technician</MenuItem>}
                <MenuItem value="bu_manager">Business Unit Admin</MenuItem>
                {!isBua && <MenuItem value="accounting">Accounting</MenuItem>}
                {!isBua && <MenuItem value="admin">Admin</MenuItem>}
              </Select>
            </FormControl>
            {addUserError && (
              <Typography variant="body2" color="error">{addUserError}</Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleAddUserClose}>Cancel</Button>
          <Button variant="contained" onClick={handleAddUserSubmit} disabled={addUserSaving}>
            {addUserSaving ? 'Creating...' : 'Create User'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(editUser)} onClose={handleEditClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ pb: 0 }}>
          Edit User
          {editUser && (
            <Box sx={{ mt: 1 }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {editUser.fullName || '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {editUser.username}
              </Typography>
            </Box>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
            <FormControl size="small" fullWidth>
              <InputLabel>Role</InputLabel>
              <Select
                value={editForm.role}
                label="Role"
                onChange={(e) => setEditForm(p => ({ ...p, role: e.target.value }))}
                MenuProps={{ disablePortal: true }}
              >
                <MenuItem value="standard">Standard</MenuItem>
                {!isBua && <MenuItem value="technician">Technician</MenuItem>}
                <MenuItem value="bu_manager">Business Unit Admin</MenuItem>
                {!isBua && <MenuItem value="accounting">Accounting</MenuItem>}
                {!isBua && <MenuItem value="admin">Admin</MenuItem>}
              </Select>
            </FormControl>
            {isBua ? (
              <>
                <TextField label="Country" size="small" fullWidth disabled value={editForm.country || '—'} />
                <TextField label="Business Unit" size="small" fullWidth disabled value={editForm.businessUnit || '—'} />
              </>
            ) : (
              <>
                <FormControl size="small" fullWidth>
                  <InputLabel>Country</InputLabel>
                  <Select
                    value={editForm.country}
                    label="Country"
                    onChange={(e) => {
                      const c = e.target.value;
                      setEditForm(p => ({ ...p, country: c, businessUnit: '', region: '' }));
                      fetch(`/api/business-units${c ? `?country=${encodeURIComponent(c)}` : ''}`)
                        .then(r => r.json()).then(d => setAvailableBusinessUnits(d.businessUnits || [])).catch(() => {});
                      setAvailableRegions([]);
                    }}
                    MenuProps={{ disablePortal: true }}
                  >
                    {availableCountriesList.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                  </Select>
                </FormControl>
                <FormControl size="small" fullWidth>
                  <InputLabel>Business Unit</InputLabel>
                  <Select
                    value={editForm.businessUnit}
                    label="Business Unit"
                    onChange={(e) => {
                      const bu = e.target.value;
                      setEditForm(p => ({ ...p, businessUnit: bu, region: '' }));
                      const params = new URLSearchParams();
                      if (bu) params.set('businessUnit', bu);
                      if (editForm.country) params.set('country', editForm.country);
                      const qs = params.toString();
                      fetch(`/api/regions${qs ? `?${qs}` : ''}`)
                        .then(r => r.json()).then(d => setAvailableRegions(d.regions || [])).catch(() => {});
                    }}
                    MenuProps={{ disablePortal: true }}
                  >
                    {availableBusinessUnits.map(b => <MenuItem key={b} value={b}>{b}</MenuItem>)}
                  </Select>
                </FormControl>
              </>
            )}
            <FormControl size="small" fullWidth>
              <InputLabel>Region</InputLabel>
              <Select
                value={editForm.region}
                label="Region"
                onChange={(e) => setEditForm(p => ({ ...p, region: e.target.value }))}
                MenuProps={{ disablePortal: true }}
              >
                {[...availableRegions, ...(!availableRegions.includes('General') ? ['General'] : [])].map(r => (
                  <MenuItem key={r} value={r}>{r}</MenuItem>
                ))}
              </Select>
            </FormControl>
            {editError && (
              <Typography variant="body2" color="error">{editError}</Typography>
            )}
          </Box>
        </DialogContent>
        {/* Four actions don't fit one phone-width row — let them wrap */}
        <DialogActions sx={{ px: { xs: 1.5, sm: 3 }, pb: 2, justifyContent: 'space-between', gap: 1, flexWrap: 'wrap' }}>
          <Button
            color="warning"
            onClick={handleResetPassword}
            disabled={actionBusy || editSaving}
            sx={{ minWidth: { xs: 72, sm: 90 } }}
          >
            Reset
          </Button>
          {editUser?.isActive === false ? (
            <Button
              color="success"
              onClick={handleRestoreUser}
              disabled={actionBusy || editSaving}
              sx={{ minWidth: { xs: 72, sm: 90 } }}
            >
              Restore
            </Button>
          ) : (
            <Button
              color="error"
              onClick={() => setDeleteConfirmOpen(true)}
              disabled={actionBusy || editSaving}
              sx={{ minWidth: { xs: 72, sm: 90 } }}
            >
              Delete
            </Button>
          )}
          <Button onClick={handleEditClose} sx={{ minWidth: 90 }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEditSubmit}
            disabled={editSaving || actionBusy}
            sx={{ minWidth: { xs: 72, sm: 90 } }}
          >
            {editSaving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmOpen} onClose={() => !actionBusy && setDeleteConfirmOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete user?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            <strong>{editUser?.fullName || editUser?.username}</strong> will no longer be able to log in.
            Their record is preserved and can be restored at any time.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)} disabled={actionBusy}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteUser} disabled={actionBusy}>
            {actionBusy ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(resetResult)} onClose={() => setResetResult(null)} maxWidth="xs" fullWidth>
        <DialogTitle>New temporary password</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Copy this password now — it will not be shown again. Share it with the user securely.
          </Alert>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <TextField
              value={resetResult?.password || ''}
              size="small"
              fullWidth
              InputProps={{ readOnly: true, sx: { fontFamily: 'monospace', fontWeight: 600 } }}
            />
            <Tooltip title="Copy">
              <IconButton onClick={handleCopyPassword}>
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={() => { setResetResult(null); handleEditClose(); }}>Done</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={copySnack}
        autoHideDuration={1800}
        onClose={() => setCopySnack(false)}
        message="Password copied"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
}

export default ManageUsersPage;
